<template>
  <div class="upload-completed-summary">
    <ModalParagraph>{{ description }}</ModalParagraph>
    <div class="upload-completed-summary__cards">
      <UploadSummaryCard
        variant="green"
        :value="newUsersCount"
        :label="$t('userUpload.SYNC_SUMMARY_NEW_USERS')"
      >
        <template #icon>
          <PlusIcon />
        </template>
      </UploadSummaryCard>

      <UploadSummaryCard
        variant="blue"
        :value="existingUsersCount"
        :label="$t('userUpload.SYNC_SUMMARY_EXISTING_USERS')"
      >
        <template #icon>
          <SyncIcon />
        </template>
      </UploadSummaryCard>

      <UploadSummaryCard
        variant="grey"
        :value="invalidUsersCount"
        :label="$t('userUpload.SYNC_SUMMARY_INVALID_USERS')"
      >
        <template #icon>
          <WarningIcon />
        </template>
      </UploadSummaryCard>
    </div>

    <dl class="upload-completed-summary__timing">
      <div
        v-if="startedAt"
        class="upload-completed-summary__timing-row"
      >
        <dt>{{ $t('userUpload.SYNC_SUMMARY_STARTED_AT') }}</dt>
        <dd>{{ formatDateTime(startedAt) }}</dd>
      </div>
      <div
        v-if="endedAt"
        class="upload-completed-summary__timing-row"
      >
        <dt>{{ $t('userUpload.SYNC_SUMMARY_ENDED_AT') }}</dt>
        <dd>{{ formatDateTime(endedAt) }}</dd>
      </div>
      <div
        v-if="runningTime != null"
        class="upload-completed-summary__timing-row"
      >
        <dt>{{ $t('userUpload.SYNC_SUMMARY_RUNNING_TIME') }}</dt>
        <dd>{{ formatDuration(runningTime) }}</dd>
      </div>
    </dl>
  </div>
</template>

<script setup lang="ts">
import PlusIcon from '@/components/icon_components/PlusIcon.vue'
import SyncIcon from '@/components/icon_components/SyncIcon.vue'
import WarningIcon from '@/components/icon_components/WarningIcon.vue'
import ModalParagraph from '@/components/generics/modals/ModalParagraph.vue'
import { formatDuration } from '@/helpers/formatDuration'
import { useDateTimeFormat } from '@/hooks/useDateTimeFormat'
import { useI18n } from '@/hooks/useI18N'
import type { UserUploadState } from '@/queries/userUpload/userUploadTypes'
import { computed } from 'vue'
import UploadSummaryCard from './UploadSummaryCard.vue'

const props = defineProps<{
  state: UserUploadState
  newUsersCount: number
  existingUsersCount: number
  invalidUsersCount: number
  startedAt: string | null
  endedAt: string | null
  runningTime: number | null
}>()

const i18n = useI18n()

const { formatDateTime } = useDateTimeFormat()

const descriptionKeys: Record<UserUploadState, string> = {
  initialized: 'userUpload.SYNC_STATUS_INITIALIZED',
  started: 'userUpload.SYNC_STATUS_STARTED',
  stopped: 'userUpload.SYNC_STATUS_STOPPED',
  halted: 'userUpload.SYNC_STATUS_HALTED',
}

const description = computed(() => i18n.t(descriptionKeys[props.state]))
</script>

<style lang="scss" scoped>
.upload-completed-summary {
  display: flex;
  flex-direction: column;
  gap: $spacing-s;

  &__cards {
    display: flex;
    gap: $spacing-2xs;
  }

  &__timing {
    display: flex;
    flex-direction: column;
    gap: $spacing-5xs;
    margin: 0;
  }

  &__timing-row {
    display: flex;
    justify-content: space-between;

    dt {
      font-size: $font-size-s;
      color: $color-text-light;
    }

    dd {
      font-size: $font-size-s;
      font-weight: 700;
      color: $color-text;
      margin: 0;
    }
  }
}
</style>
